import {
  QueryClientProvider,
  MutationCache,
  QueryClient,
  QueryCache,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useTranslationsProvider } from '@mfw/components';
import React from 'react';

import {
  handleGlobalMutationError,
  handleGlobalQueryError,
} from './globalErrorHandlers';
import { setApiLocale } from '../api/axios';
import config from '../config';

export interface ApiClientProviderProps {
  children: React.ReactNode;
}

let queryClientInstance: QueryClient | null = null;

getQueryClient();

export function getQueryClientLoaderInstance() {
  return queryClientInstance;
}

function getQueryClient() {
  if (!queryClientInstance) {
    queryClientInstance = new QueryClient({
      defaultOptions: {
        mutations: {
          // FAIL, DO NOT QUEUE. The default 'online' mode PAUSES a mutation
          // started while the browser is offline: it never settles, so every
          // dialog that disables Cancel while `isLoading` becomes an
          // inescapable modal. Axios fails fast offline on its own and no
          // mutation here relies on pause/queue semantics, so failing lets
          // the error toast explain and the dialog close.
          networkMode: 'always',
          retry: false,
          throwOnError: () => {
            return false;
          },
        },
        queries: {
          // Disables cache garbage collection
          gcTime: Infinity,
          refetchOnReconnect: true,
          refetchOnWindowFocus: false,
          retry: false,
          // Data is going to be refreshed after 2 minutes if they are stale
          staleTime: 2 * 60 * 1000,
          throwOnError: () => {
            return false;
          },
        },
      },
      // The app-wide floor under every failed write and read — see
      // `globalErrorHandlers`. Both configs used to be empty, which made a
      // mutation without its own `onError` (126 of 173) fail in complete
      // silence unless the failure happened to be an API refusal.
      mutationCache: new MutationCache({ onError: handleGlobalMutationError }),
      queryCache: new QueryCache({ onError: handleGlobalQueryError }),
    });
    return queryClientInstance;
  }
}

const ApiClientProvider = ({ children }: ApiClientProviderProps) => {
  const [queryClient] = React.useState(() => {
    return queryClientInstance as QueryClient;
  });

  // API requests advertise the app language (Accept-Language) so translated
  // BE responses match the UI instead of the browser default
  const { locale } = useTranslationsProvider();
  React.useEffect(() => {
    setApiLocale(locale);
  }, [locale]);

  return (
    <QueryClientProvider client={queryClient}>
      {config.environment.isLocal && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
      {children}
    </QueryClientProvider>
  );
};

export default ApiClientProvider;
